// One ticket: who wrote, the clock, what can be done, the whole thread with its
// notes and history in time order, and the reply box.
//
// A REPLY IS SENT BY A PERSON. The Send button is the only way anything leaves,
// and it goes out from admin@fleetly.co.za through Gmail, threaded into the
// requester's conversation. An internal note never leaves the building and
// never stops the clock; the box says which of the two you are writing.
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Lock, Paperclip, Send, StickyNote, Timer } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  useAddNote,
  useAssignTicket,
  useGetAttachmentUrl,
  useSendReply,
  useSetCategory,
  useSetFiltered,
  useSetStatus,
} from '../../hooks/backend/inbox'
import { clockLabel, filterLabel, formatDuration, splitQuoted, statusLabel, STATUS_OPTIONS } from '../../utils/inbox'
import { kindLabel, kindTone, messageTime } from '../../utils/whatsapp'
import type { Options, TicketDetail, TicketEvent, TicketMessage } from './types'

function eventText(e: TicketEvent): string | null {
  const who = e.actor_name ?? 'The system'
  switch (e.kind) {
    case 'created':
      return e.body ?? 'Ticket opened'
    case 'assigned':
      return e.to_name ? `${who} assigned this to ${e.to_name}` : `${who} took this off ${e.from_name ?? 'its assignee'}`
    case 'status':
      return `${who} changed the status from ${statusLabel(e.from_value ?? '')} to ${statusLabel(e.to_value ?? '')}`
    case 'reopened':
      return 'Reopened, because the requester wrote again'
    case 'category':
      return `${who} set the category to ${e.to_value ?? 'none'}`
    case 'filtered':
      return `${e.actor_name ? `${e.actor_name}: ` : ''}${e.body ?? 'Filtered'}`
    case 'unfiltered':
      return `${who}: ${e.body ?? 'Counted after all'}`
    case 'responded':
      return `${e.actor_name ?? 'admin@'} replied, which stopped the clock`
    case 'reply_failed':
      return `A reply was not sent: ${e.body ?? ''}`
    default:
      return null
  }
}

function MessageBubble({ m, onAttachment }: { m: TicketMessage; onAttachment: (id: string) => void }) {
  const [showQuoted, setShowQuoted] = useState(false)
  const { fresh, quoted } = splitQuoted(m.body_text)
  const mine = m.direction !== 'in'
  const who =
    m.direction === 'in'
      ? m.from_name ? `${m.from_name} <${m.from_email}>` : m.from_email
      : m.sent_by_name
        ? `${m.sent_by_name}, from ${m.from_email}`
        : m.sent_via === 'system'
          ? `Sent automatically by the platform, from ${m.from_email}`
          : `${m.from_email} (sent from Gmail)`
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[92%] rounded-lg border px-3 py-2 text-sm md:max-w-[80%]',
          m.direction === 'in' && 'border-border bg-card',
          m.direction === 'out' && m.sent_via === 'person' && 'border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand-soft))]',
          m.direction === 'out' && m.sent_via === 'system' && 'border-dashed border-border bg-muted/40',
          m.direction === 'internal' && 'border-dashed border-border bg-muted/40',
        )}
      >
        <p className="text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">{who}</span> · {messageTime(m.message_at)}
        </p>
        {(m.to.length > 0 || m.cc.length > 0) && (
          <p className="truncate text-[11px] text-muted-foreground">
            To {m.to.join(', ')}
            {m.cc.length ? `, cc ${m.cc.join(', ')}` : ''}
          </p>
        )}
        <div className="mt-1 whitespace-pre-wrap break-words text-foreground">{fresh || <i>(no text)</i>}</div>
        {quoted && (
          <button type="button" className="mt-1 text-[11px] text-muted-foreground underline" onClick={() => setShowQuoted((v) => !v)}>
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
        )}
        {showQuoted && <div className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">{quoted}</div>}
        {m.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.attachments.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onAttachment(a.id)}
                className="inline-flex max-w-full items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
              >
                <Paperclip className="h-3 w-3 shrink-0" />
                <span className="truncate">{a.filename ?? 'attachment'}</span>
              </button>
            ))}
          </div>
        )}
        {m.send_status === 'sending' && <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">Sending…</p>}
        {m.send_status === 'failed' && (
          <p className="mt-1 text-[11px] text-rose-700 dark:text-rose-300">Not sent. {m.failure_reason}</p>
        )}
      </div>
    </div>
  )
}

export function TicketPanel({
  detail,
  loading,
  error,
  options,
  onBack,
  onChanged,
}: {
  detail: TicketDetail | null
  loading: boolean
  error: string | null
  options: Options | null
  onBack: () => void
  onChanged: () => void
}) {
  const setStatus = useSetStatus()
  const assign = useAssignTicket()
  const setCategory = useSetCategory()
  const setFiltered = useSetFiltered()
  const addNote = useAddNote()
  const sendReply = useSendReply<{ ok: boolean; to: string; cc: string[] }>()
  const attachmentUrl = useGetAttachmentUrl<{ url: string }>()

  const [mode, setMode] = useState<'reply' | 'note'>('reply')
  const [text, setText] = useState('')
  const [replyAll, setReplyAll] = useState(false)
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  const ticketId = detail?.id ?? null

  useEffect(() => {
    setText('')
    setNotice(null)
    setReplyAll(false)
    setMode('reply')
  }, [ticketId])

  const timeline = useMemo(() => {
    if (!detail) return []
    const items: ({ at: string; type: 'm'; m: TicketMessage } | { at: string; type: 'e'; e: TicketEvent })[] = [
      ...detail.messages.map((m) => ({ at: m.message_at, type: 'm' as const, m })),
      ...detail.events.filter((e) => e.kind !== 'responded').map((e) => ({ at: e.created_at, type: 'e' as const, e })),
    ]
    return items.sort((a, b) => a.at.localeCompare(b.at))
  }, [detail])

  if (!detail) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
        {loading ? (
          <>
            <Spinner className="h-4 w-4" /> Opening the ticket…
          </>
        ) : error ? (
          <span className="text-rose-700 dark:text-rose-300">{error}</span>
        ) : (
          'Choose a ticket on the left.'
        )}
      </div>
    )
  }

  const t = detail.ticket
  const c = clockLabel(t)
  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setNotice(null)
    try {
      await fn()
      if (done) setNotice({ ok: true, text: done })
      onChanged()
    } catch (e) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) })
    }
  }

  const lastIn = [...detail.messages].reverse().find((m) => m.direction === 'in')
  const replyTo = lastIn?.from_email ?? t.requester_email
  const others = lastIn
    ? [...lastIn.to, ...lastIn.cc].filter((a) => a && a !== options?.sync?.mailbox && a !== replyTo)
    : []

  const onSend = (thenResolve: boolean) =>
    run(async () => {
      const res = await sendReply.trigger({ id: t.id, body: text, reply_all: replyAll })
      if (thenResolve) await setStatus.trigger({ id: t.id, status: 'resolved' })
      setText('')
      setNotice({
        ok: true,
        text: `Sent to ${res?.to ?? replyTo}${res?.cc?.length ? ` and ${res.cc.length} more` : ''}${thenResolve ? ', and resolved' : ''}.`,
      })
    })

  const onNote = () =>
    run(async () => {
      await addNote.trigger({ id: t.id, body: text })
      setText('')
    }, 'Note saved. It is visible to staff only.')

  const openAttachment = (id: string) =>
    run(async () => {
      const res = await attachmentUrl.trigger({ id }, { skipCache: true })
      if (res?.url) window.open(res.url, '_blank', 'noopener')
    })

  const busy = sendReply.loading || addNote.loading

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b border-border px-3 py-2">
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
            aria-label="Back to the queue"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">
              <span className="text-muted-foreground">#{t.ref} </span>
              {t.subject ?? '(no subject)'}
            </p>
            <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <span>{t.requester_name ? `${t.requester_name} <${t.requester_email}>` : t.requester_email}</span>
              <span>Received {messageTime(t.first_received_at)}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {detail.contact?.person_id ? (
            <>
              <Chip tone={kindTone(detail.contact.contact_kind)}>
                {kindLabel(detail.contact.contact_kind)}
                {detail.contact.person_name ? `: ${detail.contact.person_name}` : ''}
              </Chip>
              {detail.contact.record_route && (
                <Button asChild size="sm" variant="outline" className="h-7">
                  <Link to={detail.contact.record_route}>
                    <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open record
                  </Link>
                </Button>
              )}
            </>
          ) : (
            <span
              className="text-xs italic text-muted-foreground"
              title="No person on file has this exact address as their primary email. Nothing is guessed."
            >
              Unmatched
            </span>
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium tabular-nums',
              c.tone === 'overdue' && 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]',
              c.tone === 'soon' && 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
              c.tone === 'ok' && 'text-emerald-700 dark:text-emerald-300',
              (c.tone === 'paused' || c.tone === 'none') && 'text-muted-foreground',
            )}
          >
            <Timer className="h-3.5 w-3.5" /> {c.label}
          </span>
          {t.first_response_at && (
            <span className="text-xs text-muted-foreground">
              First reply after {formatDuration(t.first_response_clock_seconds ?? 0)}
              {t.first_response_by_name ? ` by ${t.first_response_by_name}` : ''}
              {t.first_response_in_time === false ? `, outside the ${t.hours} hours` : ''}
            </span>
          )}
          {t.filter_reason && <Chip tone="neutral">Filtered: {filterLabel(t.filter_reason)}</Chip>}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <NativeSelect
            aria-label="Status"
            value={t.status}
            onChange={(e) => void run(() => setStatus.trigger({ id: t.id, status: e.target.value }))}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Assigned to"
            value={t.assigned_to ?? ''}
            onChange={(e) => void run(() => assign.trigger({ id: t.id, person_id: e.target.value || null }))}
          >
            <option value="">Unassigned</option>
            {(options?.assignees ?? []).map((a) => (
              <option key={a.person_id} value={a.person_id}>
                {a.name}
                {a.can_use_inbox ? '' : ' (cannot open the Inbox)'}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Category"
            value={t.category ?? ''}
            onChange={(e) => void run(() => setCategory.trigger({ id: t.id, category: e.target.value || null }))}
          >
            <option value="">No category</option>
            {(options?.categories ?? []).map((cat) => (
              <option key={cat.key} value={cat.key}>
                {cat.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-wrap gap-2">
          {t.filter_reason ? (
            <Button size="sm" variant="outline" className="h-7" onClick={() => void run(() => setFiltered.trigger({ id: t.id, filtered: false }), 'Counted. The clock runs from the last message from the requester.')}>
              This needs an answer, count it
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" onClick={() => void run(() => setFiltered.trigger({ id: t.id, filtered: true }), 'Filtered. It no longer counts against the clock.')}>
              Not a person waiting, do not count
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <div
          className={cn(
            'border-b border-border px-4 py-1.5 text-xs',
            notice.ok ? 'bg-muted/50 text-muted-foreground' : 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
          )}
        >
          {notice.text}
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-muted/30 p-3">
        {timeline.map((item) =>
          item.type === 'm' ? (
            <MessageBubble key={`m${item.m.id}`} m={item.m} onAttachment={(id) => void openAttachment(id)} />
          ) : item.e.kind === 'note' ? (
            <div key={`e${item.e.id}`} className="mx-auto max-w-[92%] rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950">
              <p className="flex items-center gap-1 text-[11px] text-amber-800 dark:text-amber-200">
                <Lock className="h-3 w-3" /> Internal note by {item.e.actor_name ?? 'someone'} · {messageTime(item.e.created_at)}
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words text-foreground">{item.e.body}</p>
            </div>
          ) : (
            eventText(item.e) && (
              <p key={`e${item.e.id}`} className="text-center text-[11px] text-muted-foreground">
                {eventText(item.e)} · {messageTime(item.e.created_at)}
              </p>
            )
          ),
        )}
      </div>

      <div className="border-t border-border bg-card p-3">
        <div className="mb-2 flex gap-1">
          <button
            type="button"
            onClick={() => setMode('reply')}
            className={cn('inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium', mode === 'reply' ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]' : 'text-muted-foreground')}
          >
            <Send className="h-3.5 w-3.5" /> Reply to requester
          </button>
          <button
            type="button"
            onClick={() => setMode('note')}
            className={cn('inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium', mode === 'note' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' : 'text-muted-foreground')}
          >
            <StickyNote className="h-3.5 w-3.5" /> Internal note
          </button>
        </div>
        {mode === 'reply' && t.channel !== 'email' ? (
          <p className="text-xs text-muted-foreground">Replies on this channel are sent from the WhatsApp page for now.</p>
        ) : (
          <>
            {mode === 'reply' && (
              <p className="mb-1 text-[11px] text-muted-foreground">
                From admin@fleetly.co.za to {replyTo}
                {replyAll && others.length ? `, cc ${others.join(', ')}` : ''}. Your name is added under it.
              </p>
            )}
            {mode === 'note' && (
              <p className="mb-1 text-[11px] text-muted-foreground">Staff only. Never emailed, and does not stop the clock.</p>
            )}
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder={mode === 'reply' ? 'Write your reply' : 'Write a note for the team'}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {mode === 'reply' && others.length > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Checkbox checked={replyAll} onCheckedChange={(v) => setReplyAll(v === true)} /> Reply all ({others.length} more)
                </label>
              )}
              <div className="flex-1" />
              {mode === 'reply' ? (
                <>
                  <Button size="sm" variant="outline" disabled={busy || !text.trim()} onClick={() => void onSend(true)}>
                    Send and resolve
                  </Button>
                  <Button size="sm" variant="brand" disabled={busy || !text.trim()} onClick={() => void onSend(false)}>
                    {sendReply.loading ? <Spinner className="mr-1.5 h-3.5 w-3.5" /> : <Send className="mr-1.5 h-3.5 w-3.5" />} Send
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" disabled={busy || !text.trim()} onClick={() => void onNote()}>
                  Save note
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
